import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, ChartBar, Gear, EnvelopeSimple, ImageSquare, Trash, Link as LinkIcon, Trophy, DeviceMobileSpeaker, Pencil, X, Check } from '@phosphor-icons/react';

export default function AdminDashboard() {
  const { API } = useAuth();
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: ''
  });
  const [users, setUsers] = useState([]);
  
  // Custom Ads State
  const [ads, setAds] = useState([]);
  const [newAdLink, setNewAdLink] = useState('');
  const [newAdLocation, setNewAdLocation] = useState('ALL INDIA');
  const [newAdSequence, setNewAdSequence] = useState(1);
  const [newAdExpiry, setNewAdExpiry] = useState('');
  const [newAdFile, setNewAdFile] = useState(null);
  const [uploadingAd, setUploadingAd] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [editFormData, setEditFormData] = useState({ link: '', location: '', sequence_number: 1, expires_at: '' });

  const [loadingSettings, setLoadingSettings] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState('');

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setMessage('');
    try {
      const { data } = await axios.post(`${API}/admin/settings/test-email`, settings, { withCredentials: true });
      setMessage({ text: data.detail, type: 'success' });
    } catch (e) {
      setMessage({ text: e.response?.data?.detail || 'SMTP Test Failed.', type: 'error' });
    } finally {
      setTestingEmail(false);
    }
  };
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchAds = async () => {
    try {
      const adsRes = await axios.get(`${API}/admin/ads`, { withCredentials: true });
      setAds(adsRes.data);
    } catch (e) {
      console.error(e);
    }
  }

  const fetchData = async () => {
    try {
      const statsRes = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(statsRes.data);
      
      const settingsRes = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      if (settingsRes.data) {
        setSettings(prev => ({...prev, ...settingsRes.data}));
      }

      const usersRes = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(usersRes.data);

      fetchAds();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoadingSettings(true);
    setMessage('');
    try {
      await axios.put(`${API}/admin/settings`, settings, { withCredentials: true });
      setMessage('Settings saved successfully.');
    } catch (e) {
      setMessage('Failed to save settings.');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleUploadAd = async (e) => {
    e.preventDefault();
    if (!newAdFile || !newAdLink) return;
    
    setUploadingAd(true);
    const formData = new FormData();
    formData.append('link', newAdLink);
    formData.append('location', newAdLocation);
    formData.append('sequence_number', newAdSequence);
    if (newAdExpiry) {
      formData.append('expires_at', newAdExpiry);
    }
    formData.append('file', newAdFile);

    try {
      await axios.post(`${API}/admin/ads`, formData, { 
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewAdLink('');
      setNewAdLocation('ALL INDIA');
      setNewAdSequence(1);
      setNewAdExpiry('');
      setNewAdFile(null);
      fetchAds();
    } catch (e) {
      alert("Failed to upload ad. " + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingAd(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm("Delete this advertisement?")) return;
    try {
      await axios.delete(`${API}/admin/ads/${adId}`, { withCredentials: true });
      fetchAds();
    } catch (e) {
      alert("Failed to delete ad.");
    }
  };

  const startEditAd = (ad) => {
    setEditingAd(ad._id);
    setEditFormData({
      link: ad.link || '',
      location: ad.location || 'ALL INDIA',
      sequence_number: ad.sequence_number || 1,
      expires_at: ad.expires_at ? ad.expires_at.split('T')[0] : ''
    });
  };

  const cancelEditAd = () => {
    setEditingAd(null);
  };

  const handleUpdateAd = async (adId) => {
    try {
      await axios.put(`${API}/admin/ads/${adId}`, editFormData, { withCredentials: true });
      setEditingAd(null);
      fetchAds();
    } catch (e) {
      alert("Failed to update ad. " + (e.response?.data?.detail || e.message));
    }
  };

  const handleBroadcastSMS = async (e) => {
    e.preventDefault();
    if (!broadcastMsg) return;
    if (!window.confirm("Send this SMS to ALL verified phone numbers?")) return;
    
    setBroadcasting(true);
    setBroadcastResult('');
    try {
      const { data } = await axios.post(`${API}/admin/broadcast-sms`, { message: broadcastMsg }, { withCredentials: true });
      setBroadcastResult({ text: data.message, type: 'success' });
      setBroadcastMsg('');
    } catch (e) {
      setBroadcastResult({ text: e.response?.data?.detail || 'Failed to send broadcast.', type: 'error' });
    } finally {
      setBroadcasting(false);
    }
  };

  if (!stats) return <div className="container py-12 text-center text-primary uppercase tracking-widest font-bold animate-pulse">VERIFYING CLEARANCE...</div>;

  return (
    <div className="container py-8 max-w-6xl space-y-8" data-testid="admin-page">
      <div className="flex items-center gap-4 border-b border-border pb-6">
        <Gear size={48} weight="duotone" className="text-chart-4" />
        <div>
          <h1 className="text-4xl font-heading text-white uppercase tracking-widest">MASTER CONTROL</h1>
          <p className="text-muted-foreground font-bold tracking-widest text-sm uppercase">Platform Administration & Monetization</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-black/50 border border-white/10 rounded-none h-14 p-1 mb-8 w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" className="rounded-none h-full font-heading tracking-widest uppercase data-[state=active]:bg-primary data-[state=active]:text-white px-6">Overview</TabsTrigger>
          <TabsTrigger value="monetization" className="rounded-none h-full font-heading tracking-widest uppercase data-[state=active]:bg-chart-5 data-[state=active]:text-background px-6">Custom Ads</TabsTrigger>
          <TabsTrigger value="email" className="rounded-none h-full font-heading tracking-widest uppercase data-[state=active]:bg-chart-4 data-[state=active]:text-background px-6">SMTP / Emails</TabsTrigger>
          <TabsTrigger value="sms" className="rounded-none h-full font-heading tracking-widest uppercase data-[state=active]:bg-chart-3 data-[state=active]:text-background px-6">Broadcast SMS</TabsTrigger>
          <TabsTrigger value="users" className="rounded-none h-full font-heading tracking-widest uppercase data-[state=active]:bg-primary data-[state=active]:text-white px-6">User Database</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="rounded-none border-border bg-card">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">TOTAL RECRUITS</p>
                  <p className="text-4xl font-heading text-white">{stats.total_users}</p>
                </div>
                <Users size={32} className="text-chart-3" />
              </CardContent>
            </Card>
            <Card className="rounded-none border-border bg-card">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">MOCK TESTS GENERATED</p>
                  <p className="text-4xl font-heading text-white">{stats.total_tests_generated}</p>
                </div>
                <ChartBar size={32} className="text-primary" />
              </CardContent>
            </Card>
            <Card className="rounded-none border-border bg-card">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">TESTS COMPLETED</p>
                  <p className="text-4xl font-heading text-white">{stats.total_tests_taken}</p>
                </div>
                <Trophy size={32} className="text-chart-5" />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="monetization" className="space-y-6">
          <Card className="rounded-none border-chart-5/50 bg-black/50">
            <CardHeader>
              <CardTitle className="font-heading text-2xl tracking-widest uppercase flex items-center gap-2 text-chart-5"><ImageSquare /> Upload Direct Advertisement</CardTitle>
              <CardDescription className="text-muted-foreground font-bold tracking-widest uppercase text-xs">Upload targeted banners. Geo-tag ads by state, and assign a sequence order so students see them progressively.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUploadAd} className="space-y-4 max-w-2xl">
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">DESTINATION URL (LINK)</label>
                  <Input required type="url" value={newAdLink} onChange={e => setNewAdLink(e.target.value)} placeholder="https://advertiser.com/campaign" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
                </div>
                
                <div className="flex gap-4">
                  <div className="w-1/3">
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">TARGET LOCATION</label>
                    <Input required type="text" value={newAdLocation} onChange={e => setNewAdLocation(e.target.value)} placeholder="ALL INDIA, Karnataka, Delhi" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white uppercase" />
                  </div>
                  <div className="w-1/3">
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">SEQUENCE NUMBER</label>
                    <Input required type="number" min="1" value={newAdSequence} onChange={e => setNewAdSequence(parseInt(e.target.value))} className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
                  </div>
                  <div className="w-1/3">
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">EXPIRATION DATE (OPTIONAL)</label>
                    <Input type="date" value={newAdExpiry} onChange={e => setNewAdExpiry(e.target.value)} className="mt-1 rounded-none border-border bg-black/50 h-12 text-white [color-scheme:dark]" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">AD BANNER IMAGE</label>
                  <Input required type="file" accept="image/*" onChange={e => setNewAdFile(e.target.files[0])} className="mt-1 rounded-none border-border bg-black/50 h-12 text-white pt-2.5 cursor-pointer" />
                </div>

                {newAdFile && (
                  <div className="border border-chart-5/30 bg-chart-5/5 p-4 relative">
                    <p className="text-xs font-bold text-chart-5 uppercase tracking-widest mb-2">LIVE PREVIEW</p>
                    <img src={URL.createObjectURL(newAdFile)} alt="Ad Preview" className="w-full max-h-[120px] object-cover border border-border" />
                  </div>
                )}

                <Button type="submit" disabled={uploadingAd || !newAdFile} className="h-12 px-8 rounded-none font-bold tracking-widest uppercase bg-chart-5 text-black hover:bg-chart-5/80">
                  {uploadingAd ? 'UPLOADING...' : 'SAVE & PUBLISH CAMPAIGN'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="rounded-none border-border bg-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl tracking-widest uppercase">Active Campaigns</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4">
                {ads.map(ad => (
                  <div key={ad._id} className="border border-border bg-black p-4 relative group flex flex-col justify-between">
                    {editingAd === ad._id ? (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="text-sm font-heading tracking-widest uppercase text-chart-4">EDITING CAMPAIGN</h4>
                          <button onClick={cancelEditAd} className="text-muted-foreground hover:text-white"><X size={20}/></button>
                        </div>
                        <img src={`${API}/public/ads/image/${ad._id}`} alt="Ad" className="w-full h-24 object-cover opacity-50 grayscale border border-border" />
                        <div>
                          <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">LINK</label>
                          <Input value={editFormData.link} onChange={e => setEditFormData({...editFormData, link: e.target.value})} className="h-8 rounded-none border-border bg-secondary text-white text-xs" />
                        </div>
                        <div className="flex gap-2">
                          <div className="w-1/3">
                            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">LOCATION</label>
                            <Input value={editFormData.location} onChange={e => setEditFormData({...editFormData, location: e.target.value})} className="h-8 rounded-none border-border bg-secondary text-white text-xs uppercase" />
                          </div>
                          <div className="w-1/3">
                            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">SEQUENCE</label>
                            <Input type="number" min="1" value={editFormData.sequence_number} onChange={e => setEditFormData({...editFormData, sequence_number: parseInt(e.target.value)})} className="h-8 rounded-none border-border bg-secondary text-white text-xs" />
                          </div>
                          <div className="w-1/3">
                            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">EXPIRES AT</label>
                            <Input type="date" value={editFormData.expires_at} onChange={e => setEditFormData({...editFormData, expires_at: e.target.value})} className="h-8 rounded-none border-border bg-secondary text-white text-xs [color-scheme:dark]" />
                          </div>
                        </div>
                        <Button onClick={() => handleUpdateAd(ad._id)} className="w-full h-10 rounded-none bg-primary hover:bg-primary/80 text-white font-bold tracking-widest uppercase text-xs flex items-center gap-2">
                          <Check size={16} /> SAVE CHANGES
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="absolute top-0 right-0 bg-primary text-black font-black text-[10px] px-2 py-1 z-10">SEQ #{ad.sequence_number || 1}</div>
                          <div className="absolute top-0 left-0 bg-chart-5 text-black font-black text-[10px] px-2 py-1 z-10 uppercase">{ad.location || 'ALL INDIA'}</div>
                          <img src={`${API}/public/ads/image/${ad._id}`} alt="Ad" className="w-full h-32 object-cover opacity-80 group-hover:opacity-100 transition-opacity mt-4" />
                        </div>
                        <div className="mt-4 flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <a href={ad.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-primary hover:underline truncate max-w-[70%]">
                              <LinkIcon /> {ad.link}
                            </a>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" className="rounded-none h-8 px-2 border-primary/50 text-primary hover:bg-primary/10" onClick={() => startEditAd(ad)}>
                                <Pencil size={16} />
                              </Button>
                              <Button variant="destructive" size="sm" className="rounded-none h-8 px-2" onClick={() => handleDeleteAd(ad._id)}>
                                <Trash size={16} />
                              </Button>
                            </div>
                          </div>
                          {ad.expires_at && (
                            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                              Expires: {new Date(ad.expires_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {ads.length === 0 && <div className="col-span-full p-8 border border-dashed border-border text-center text-muted-foreground font-bold tracking-widest uppercase text-sm">NO ACTIVE CAMPAIGNS</div>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <Card className="rounded-none border-chart-4/50 bg-black/50">
            <CardHeader>
              <CardTitle className="font-heading text-2xl tracking-widest uppercase flex items-center gap-2 text-chart-4"><EnvelopeSimple /> SMTP Mail Setup</CardTitle>
              <CardDescription className="text-muted-foreground font-bold tracking-widest uppercase text-xs">Required for sending &quot;Forgot Password&quot; recovery links</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSettings} className="space-y-4 max-w-xl">
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">SMTP HOST</label>
                  <Input value={settings.smtp_host} onChange={e => setSettings({...settings, smtp_host: e.target.value})} placeholder="smtp.gmail.com or smtp.sendgrid.net" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">SMTP PORT</label>
                  <Input type="number" value={settings.smtp_port} onChange={e => setSettings({...settings, smtp_port: parseInt(e.target.value) || 587})} placeholder="587" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">SMTP USERNAME / EMAIL</label>
                  <Input value={settings.smtp_user} onChange={e => setSettings({...settings, smtp_user: e.target.value})} placeholder="apikey or your_email@gmail.com" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">SMTP PASSWORD / APP PASSWORD</label>
                  <Input type="password" value={settings.smtp_password} onChange={e => setSettings({...settings, smtp_password: e.target.value})} placeholder="••••••••••••" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">&quot;FROM&quot; EMAIL ADDRESS (OPTIONAL)</label>
                  <Input value={settings.from_email} onChange={e => setSettings({...settings, from_email: e.target.value})} placeholder="noreply@mockrank.com" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
                </div>

                {message && <div className={`text-xs font-bold uppercase tracking-widest ${message.type === 'error' ? 'text-destructive' : 'text-chart-4'}`}>{message.text || message}</div>}

                <div className="flex gap-4 mt-4">
                  <Button type="submit" disabled={loadingSettings} className="h-12 px-8 rounded-none font-bold tracking-widest uppercase bg-chart-4 text-black hover:bg-chart-4/80">
                    {loadingSettings ? 'SAVING...' : 'SAVE SMTP SECRETS'}
                  </Button>
                  
                  <Button type="button" variant="outline" disabled={testingEmail} onClick={handleTestEmail} className="h-12 px-8 rounded-none font-bold tracking-widest uppercase border-chart-4 text-chart-4 hover:bg-chart-4/10">
                    {testingEmail ? 'TESTING...' : 'SEND TEST EMAIL'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sms">
          <Card className="rounded-none border-chart-3/50 bg-black/50">
            <CardHeader>
              <CardTitle className="font-heading text-2xl tracking-widest uppercase flex items-center gap-2 text-chart-3"><DeviceMobileSpeaker /> Broadcast SMS</CardTitle>
              <CardDescription className="text-muted-foreground font-bold tracking-widest uppercase text-xs">Send manual announcements or motivational quotes to all users with a verified phone number.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBroadcastSMS} className="space-y-4 max-w-xl">
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">SMS MESSAGE CONTENT</label>
                  <textarea 
                    value={broadcastMsg} 
                    onChange={e => setBroadcastMsg(e.target.value)} 
                    placeholder="Enter your daily quote or exam reminder here..." 
                    className="w-full min-h-[120px] bg-black/50 border border-border p-4 text-sm text-white focus:border-chart-3 focus:ring-1 focus:ring-chart-3 outline-none mt-1"
                    required
                  />
                  <p className="mt-2 text-xs text-muted-foreground flex justify-between">
                    <span>Keep under 160 characters for a single SMS segment.</span>
                    <span className={broadcastMsg.length > 160 ? 'text-chart-2 font-bold' : ''}>{broadcastMsg.length} / 160</span>
                  </p>
                </div>

                {broadcastResult && <div className={`text-xs font-bold uppercase tracking-widest ${broadcastResult.type === 'error' ? 'text-destructive' : 'text-chart-5'}`}>{broadcastResult.text}</div>}

                <Button type="submit" disabled={broadcasting || !broadcastMsg} className="h-12 px-8 rounded-none font-bold tracking-widest uppercase bg-chart-3 text-black hover:bg-chart-3/80 mt-4">
                  {broadcasting ? 'TRANSMITTING...' : 'SEND MASS BROADCAST'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="rounded-none border-border bg-card overflow-x-auto">
            <div className="min-w-[800px]">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-border bg-secondary font-bold text-xs uppercase tracking-widest text-muted-foreground">
                <div className="col-span-3">EMAIL</div>
                <div className="col-span-3">FULL NAME</div>
                <div className="col-span-2">DISPLAY ALIAS</div>
                <div className="col-span-2">TARGET EXAM</div>
                <div className="col-span-2 text-right">ROLE</div>
              </div>
              <div className="divide-y divide-border">
                {users.map(u => (
                  <div key={u._id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-secondary/30 transition-colors">
                    <div className="col-span-3 text-sm truncate">{u.email}</div>
                    <div className="col-span-3 font-bold truncate">{u.name}</div>
                    <div className="col-span-2 text-sm text-muted-foreground truncate">{u.display_name || '-'}</div>
                    <div className="col-span-2 text-xs tracking-widest uppercase border border-border px-2 py-1 bg-secondary truncate max-w-fit">{u.target_exam}</div>
                    <div className="col-span-2 text-right">
                      <span className={`text-xs font-black tracking-widest uppercase px-2 py-1 rounded-sm ${u.role === 'admin' ? 'bg-chart-4/20 text-chart-4 border border-chart-4/50' : 'bg-primary/10 text-primary border border-primary/30'}`}>
                        {u.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}